/* eslint-disable @nx/enforce-module-boundaries */
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { PushPipe } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, map, of, startWith, switchMap, tap, timer } from 'rxjs';

import { MeetingBotStatus } from '@ninety/platform/models/meetings/meeting-bot';
import { FeatureFlagFacade, FeatureFlagKeys, selectCurrentUserId } from '@ninety/platform/state';
import { TerraIconModule, TerraSwitchModule } from '@ninety/terra';
import { NinetyChipComponent } from '@ninety/ui/core';
import { MeetingStateActions } from '@ninety/web/pages/meeting/_state/meetings.actions';
import { MeetingsStateSelectors } from '@ninety/web/pages/meeting/_state/meetings.selectors';

const TERMINAL_STATUSES = [MeetingBotStatus.ended, MeetingBotStatus.failed];
const AUTO_HIDE_DELAY_MS = 5000;

@Component({
  selector: 'ninety-notetaker-control',
  templateUrl: './notetaker-control.component.html',
  styleUrl: './notetaker-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    PushPipe,
    FormsModule,
    TerraIconModule,
    TerraSwitchModule,
    MatButton,
    MatIconButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    NinetyChipComponent,
  ],
})
export class NotetakerControlComponent {
  private readonly store = inject(Store);
  private readonly featureFlags = inject(FeatureFlagFacade);

  protected readonly MeetingBotStatus = MeetingBotStatus;

  // Once a bot sits in a terminal status past its auto-hide delay, its chip stays hidden even if
  // the same status keeps re-emitting (e.g. a duplicate webhook redelivery).
  private autoHiddenBotId: string | null = null;

  protected readonly webMeetingAgentEnabled$ = this.featureFlags.getFlag(FeatureFlagKeys.webMeetingAgent);
  protected readonly currentUserId$ = this.store.select(selectCurrentUserId);
  protected readonly currentMeeting$ = this.store.select(MeetingsStateSelectors.selectCurrentMeeting);
  protected readonly currentMeetingScheduleId$ = this.store.select(
    MeetingsStateSelectors.selectCurrentMeetingScheduleId
  );

  protected readonly visible$ = this.currentMeeting$.pipe(
    map(meeting => meeting?.meetingBot),
    distinctUntilChanged((a, b) => a?.botId === b?.botId && a?.status === b?.status),
    switchMap(bot => {
      if (!bot || bot.status === MeetingBotStatus.dismissed) {
        return of(false);
      }
      if (TERMINAL_STATUSES.includes(bot.status)) {
        if (bot.botId === this.autoHiddenBotId) {
          return of(false);
        }
        return timer(AUTO_HIDE_DELAY_MS).pipe(
          tap(() => (this.autoHiddenBotId = bot.botId ?? null)),
          map(() => false),
          startWith(true)
        );
      }
      this.autoHiddenBotId = null;
      return of(true);
    })
  );

  // Shown instead of the status chip when the meeting started without Maz - lets the
  // presenter add it mid-meeting rather than only at meeting-start.
  protected readonly showAddButton$ = this.currentMeeting$.pipe(
    map(meeting => !meeting?.meetingBot && !!meeting?.inProgress)
  );

  addMeetingBot() {
    this.store.dispatch(MeetingStateActions.openAddMeetingBotDialog());
  }

  removeMeetingBot() {
    this.store.dispatch(MeetingStateActions.openRemoveMeetingBotDialog());
  }

  joinBotNow() {
    this.store.dispatch(MeetingStateActions.joinMeetingBotNow());
  }

  toggleRecording(recording: boolean) {
    this.store.dispatch(recording ? MeetingStateActions.resumeMeetingBot() : MeetingStateActions.pauseMeetingBot());
  }

  label(status: MeetingBotStatus | undefined): string {
    switch (status) {
      case MeetingBotStatus.recording:
        return 'Maz is recording...';
      case MeetingBotStatus.paused:
        return 'Maz is paused';
      case MeetingBotStatus.waiting:
        return 'Maz is waiting in the waiting room';
      case MeetingBotStatus.failed:
        return "Maz couldn't join";
      case MeetingBotStatus.ended:
        return 'Maz has left the meeting';
      case MeetingBotStatus.active:
      case MeetingBotStatus.creating:
      default:
        return 'Waiting for Maz to join';
    }
  }
}
